<template>
  <div>
    <responsive-drawer-layout>
      <template v-slot:drawer>
        <FilterListComponent :filters="moduleData.filters" :selectedFilter="selectedFilter" @update:selectedFilter="selectedFilter = $event" />
        <TagGroupsComponent
          :usedTags="usedTags"
          :selectedTags="selectedTags"
          :availableTags="availableTags"
          :selectedFilter="selectedFilter"
          @update:selectedTags="selectedTags = $event"
          @update:selectedFilter="selectedFilter = $event"
        />
      </template>
      <template v-slot:content>
        <div class="py-5 px-4">
          <v-row>
            <v-col class="filter-container">
              <v-autocomplete
                :items="currentChildren.searchTerms"
                v-model="searchQuery"
                item-value="id"
                item-text="name"
                placeholder="Search"
                prepend-inner-icon="mdi-magnify"
                label=""
                color="grey darken-2"
                filled
                rounded
                hide-details
                clearable
                auto-select-first
                hide-selected
              >
              </v-autocomplete>
              <MediaMenuSorter :value="sorterName" @input="(v) => setSorterName(v)" :customSorter="cmsSorterName" />
            </v-col>
          </v-row>
          <v-row>
            <v-col v-if="selectedFilter !== 'tags' || (selectedFilter === 'tags' && selectedTags.length === 0)" class="pt-2 pb-0">
              <v-breadcrumbs :items="breadCrumbs" class="py-0 pr-0 pl-4 pb-1">
                <template v-slot:item="{ item }">
                  <v-breadcrumbs-item :href="item.href" :disabled="currentFolderId == item.id" link>
                    {{ item.text.toUpperCase() }}
                  </v-breadcrumbs-item>
                </template>
              </v-breadcrumbs>
            </v-col>
            <v-col v-if="selectedFilter === 'tags' && selectedTags.length > 0" class="py-1 ml-4">
              <span v-for="(tagId, i) in selectedTags" :key="i">
                <v-chip v-if="tagsById[tagId]" class="mr-1" @click:close="removeTag(tagId)" small close>{{ tagsById[tagId].name }}</v-chip>
              </span>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-divider></v-divider>
              <media-module-list-layout
                v-if="currentFolderLayout === 'list'"
                @rowClick="(item) => rowClick(item)"
                :list-items="tableItems"
                :current-children="currentChildren"
              ></media-module-list-layout>

              <media-module-masonry-layout
                v-if="currentFolderLayout === 'masonry'"
                @rowClick="(item) => rowClick(item)"
                :table-items="tableItems"
                :current-children="currentChildren"
              ></media-module-masonry-layout>

              <media-module-grid-layout
                v-if="currentFolderLayout === 'grid'"
                @rowClick="(item) => rowClick(item)"
                :table-items="tableItems"
                :current-children="currentChildren"
              ></media-module-grid-layout>
            </v-col>
          </v-row>
        </div>
      </template>
    </responsive-drawer-layout>
    <v-overlay :value="loading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <v-dialog v-model="showPreview" transition="dialog-bottom-transition" fullscreen hide-overlay>
      <v-card class="fill-height">
        <v-toolbar :color="theme.appBar.other.bg" flat>
          <v-btn icon @click="onClickBackBtn" :color="color">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title class="pl-0 text-body-1" :class="textColor">{{ currentFileName }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items class="hidden-sm-and-down" style="align-items: inherit">
            <icon-text-btn
              v-if="currentFileProperties.confirm && currentFile.unconfirmed"
              @onClick="setConfirmation(currentFile.id)"
              icon="mdi-pencil"
              :text="$t('acknowledge.action')"
              :hint="$t('acknowledge.action')"
              :iconColor="color"
              :iconOnly="false"
              small
              style="cursor: pointer"
            ></icon-text-btn>
            <v-tooltip v-if="currentFileProperties.pin && !currentFile.favorite" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn :color="color" v-bind="attrs" v-on="on" @click="setFavorite(currentFile.id, true)" icon>
                  <v-icon>mdi-heart-outline</v-icon>
                </v-btn>
              </template>
              <span :class="textColor">{{ $t('mm.addtofavorites') }}</span>
            </v-tooltip>
            <v-tooltip :class="textColor" v-if="currentFileProperties.pin && currentFile.favorite" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn :color="color" v-bind="attrs" v-on="on" @click="setFavorite(currentFile.id, false)" icon>
                  <v-icon>mdi-heart</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('mm.removefromfavorites') }}</span>
            </v-tooltip>
          </v-toolbar-items>
          <HeaderMenuComponent
            :currentFileProperties="currentFileProperties"
            :currentFile="currentFile"
            @set-confirmation="setConfirmation"
            @set-favorite="setFavorite"
            v-if="displayOverflowMenu"
          />
        </v-toolbar>
        <component :is="resolveViewer(currentFile)" :container="currentFile" :media="currentMedia" />
      </v-card>
    </v-dialog>
    <!-- Dialog after leaving the media file -->
    <v-dialog v-model="acknowledgeDialog" persistent max-width="400">
      <v-card>
        <v-card-title class="text-h5">{{ $t('acknowledge.title') }}</v-card-title>
        <v-card-text
          v-html="$t('acknowledge.media.hint', { title: (fileBefore && fileBefore.properties && fileBefore.properties.name) || 'no name found' })"
        ></v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn :color="darkMode ? '' : 'accent'" text @click="closeAcknowledgeDialog">
            {{ $t('later') }}
          </v-btn>
          <v-btn :color="darkMode ? '' : 'accent'" text @click="setConfirmation(fileBefore.id, true)">
            {{ $t('acknowledge.title') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import index from '../index';
import common from '@/mixins/common';
import { mapState, mapMutations } from 'vuex';
import ResponsiveDrawerLayout from '@/layouts/ResponsiveDrawerLayout';
import {
  SORT_BY_NEWEST,
  SORT_BY_OLDEST_FIRST,
  SORT_BY_ALPHABETIC_ASC,
  SORT_BY_ALPHABETIC_DESC,
  SORT_BY_DISPLAY_START_DESC,
  SORT_BY_DISPLAY_START_ASC,
  SORT_BY_CREATED_ASC,
  SORT_BY_CREATED_DESC,
  SORT_BY_UPDATED_ASC,
  SORT_BY_UPDATED_DESC,
} from '@/common/constants';
import IconTextBtn from '../../../components/buttons/IconTextBtn.vue';

import ImageViewer from '../components/ImageViewer.vue';
import VideoViewer from '../components/VideoViewer.vue';
import AudioViewer from '../components/AudioViewer.vue';
import HTMLViewer from '../components/HTMLViewer.vue';
import ArticleViewer from '../components/ArticleViewer.vue';
import PDFViewer from '../components/PDFViewer.vue';
import UnknownViewer from '../components/UnknownViewer.vue';
import mediaModuleService from '@/modules/media/views/mediaModuleService';
import { DAY_IN_MS } from '@/common/constants';
import MediaModuleListLayout from '@/modules/media/views/MediaModulListLayout.vue';
import MediaModuleMasonryLayout from '@/modules/media/views/MediaModulMasonryLayout.vue';
import MediaModuleGridLayout from '@/modules/media/views/MediaModuleGridLayout.vue';
import FilterListComponent from '../components/FilterListComponent.vue';
import TagGroupsComponent from '../components/TagGroupsComponent.vue';
import HeaderMenuComponent from '../components/HeaderMenuComponent.vue';
import MediaMenuSorter from './MediaSorterMenu.vue';
import { SelectedFilter, IconMap, ContainerType } from '../constants';

export default {
  name: 'Media',
  components: {
    MediaModuleMasonryLayout,
    MediaModuleGridLayout,
    MediaModuleListLayout,
    ResponsiveDrawerLayout,
    ImageViewer,
    VideoViewer,
    AudioViewer,
    HTMLViewer,
    ArticleViewer,
    PDFViewer,
    UnknownViewer,
    FilterListComponent,
    TagGroupsComponent,
    HeaderMenuComponent,
    IconTextBtn,
    MediaMenuSorter,
  },
  mixins: [common],

  data() {
    return {
      selectedFilter: SelectedFilter.NONE,
      selectedTags: [],
      searchQuery: null,
      acknowledgeDialog: false,
      fileBefore: null,
      shouldGoBack: false,
    };
  },

  computed: {
    ...mapState('media', ['containersById', 'mediasById', 'tagsById', 'loading', 'sorterName', 'cmsSorterName']),
    color() {
      return this.theme.appBar.other.color;
    },
    textColor() {
      return this.theme.appBar.other.color + '--text';
    },
    currentModuleId() {
      return this.$route.params.instance || 0;
    },

    currentModule() {
      return this.containersById[this.currentModuleId];
    },

    currentFolderId() {
      return this.$route.params.folderId ? this.$route.params.folderId : this.currentModuleId;
    },

    currentFolder() {
      return this.containersById[this.currentFolderId] ? this.containersById[this.currentFolderId] : {};
    },

    currentFolderProperties() {
      return this.currentFolder.properties || {};
    },

    currentFolderLayout() {
      return this.currentFolderProperties.deviceView && this.selectedFilter === SelectedFilter.NONE ? this.currentFolderProperties.deviceView : 'list';
    },

    currentFileId() {
      return this.$route.params.fileId || 0;
    },

    currentFile() {
      return this.containersById[this.currentFileId] || {};
    },

    currentFileProperties() {
      return this.currentFile.properties || {};
    },

    currentMedia() {
      return this.currentFile.idMediaFk ? (this.mediasById[this.currentFile.idMediaFk] ? this.mediasById[this.currentFile.idMediaFk] : {}) : {};
    },

    currentChildren() {
      const folders = [];
      const files = [];
      let searchTerms = [];

      const filterHandlerMap = {
        none: this.handleNoneFilter,
        tags: this.handleTagsFilter,
      };

      const handleFilter = filterHandlerMap[this.selectedFilter] || this.handleOtherFilters;

      handleFilter(folders, files, searchTerms);

      this.sortFoldersAndFiles(folders, files);

      searchTerms = this.sortSearchTerms(searchTerms);

      return { items: [...folders, ...files], searchTerms };
    },

    breadCrumbs() {
      const items = [];
      if (this.selectedFilter === SelectedFilter.NONE) {
        this.buildBreadCrumbs(this.currentFolderId, items);
        items.reverse();
      } else {
        let message = '';

        message = this.$te(`mm.${this.selectedFilter.toLowerCase()}`) ? this.$t(`mm.${this.selectedFilter.toLowerCase()}`) : '';
        items.push({ id: this.currentModuleId, text: message, href: '' });
      }
      return items;
    },

    tableItems() {
      const parentFolderId = this.currentChildren?.items[0]?.folderId;
      let parentDeviceSort = this.containersById[parentFolderId]?.properties?.deviceSort;
      parentDeviceSort = parentDeviceSort == 'id-desc' ? 'created-desc' : parentDeviceSort;
      parentDeviceSort = parentDeviceSort == 'id-asc' ? 'created-asc' : parentDeviceSort;
      if (parentDeviceSort) {
        this.setCmsSorterName(parentDeviceSort);
      } else {
        this.setCmsSorterName('');
      }
      return this.currentChildren.items
        .map((item) => {
          let unread = 0;
          let unconfirmed = 0;

          if (item.type === ContainerType.FOLDER) {
            unread = this.countUnreadFiles(item.id);
            unconfirmed = this.countUnconfirmedFiles(item.id);
          } else {
            unread = Number(item.unread);
            unconfirmed = Number(item.unconfirmed);
          }

          const dateTime = new Date(item.createdAt);

          return {
            id: item.id,
            parentId: item.idParentFk,
            mediaId: item.idMediaFk,
            icon: this.getIcon(item.type),
            name: item.properties.name,
            description: item.properties.description,
            type: item.type,
            tags: item.tags,
            unread,
            unconfirmed,
            favorite: item.favorite ? item.favorite : false,
            time: dateTime.getTime(),
            displayFrom: item?.properties?.displayFrom ? new Date(item.properties.displayFrom)?.getTime() : null,
            createdAt: item?.createdAt,
            updatedAt: item?.updatedAt,
          };
        })
        .sort((a, b) => {
          if (this.sorterName === SORT_BY_NEWEST) {
            return b.time - a.time;
          }
          if (this.sorterName === SORT_BY_OLDEST_FIRST) {
            return a.time - b.time;
          }
          if (this.sorterName === SORT_BY_ALPHABETIC_ASC) {
            return a.name.localeCompare(b.name, undefined, {
              numeric: true,
              sensitivity: 'base',
            });
          }
          if (this.sorterName === SORT_BY_ALPHABETIC_DESC) {
            return b.name.localeCompare(a.name, undefined, {
              numeric: true,
              sensitivity: 'base',
            });
          }
          if (this.sorterName === SORT_BY_DISPLAY_START_DESC) {
            // Put nulls at the bottom
            if (a.displayFrom === null && b.displayFrom === null) {
              return b.time - a.time; // secondary sort by update date
            }
            if (a.displayFrom === null) return 1; // a goes down
            if (b.displayFrom === null) return -1; // b goes down
            return b.displayFrom - a.displayFrom;
          }
          if (this.sorterName === SORT_BY_DISPLAY_START_ASC) {
            if (a.displayFrom === null && b.displayFrom === null) {
              return a.time - b.time;
            }
            if (a.displayFrom === null) return 1;
            if (b.displayFrom === null) return -1;
            return a.displayFrom - b.displayFrom;
          }
          if (this.sorterName === SORT_BY_CREATED_ASC) {
            const date1 = Date.parse(a?.createdAt ?? '') || 0;
            const date2 = Date.parse(b?.createdAt ?? '') || 0;
            return date1 - date2;
          }
          if (this.sorterName === SORT_BY_CREATED_DESC) {
            const date1 = Date.parse(a?.createdAt ?? '') || 0;
            const date2 = Date.parse(b?.createdAt ?? '') || 0;
            return date2 - date1;
          }
          if (this.sorterName === SORT_BY_UPDATED_ASC) {
            const date1 = Date.parse(a?.updatedAt ?? '') || 0;
            const date2 = Date.parse(b?.updatedAt ?? '') || 0;
            return date1 - date2;
          }
          if (this.sorterName === SORT_BY_UPDATED_DESC) {
            const date1 = Date.parse(a?.updatedAt ?? '') || 0;
            const date2 = Date.parse(b?.updatedAt ?? '') || 0;
            return date2 - date1;
          }
          return 0;
        });
    },

    currentFileName() {
      return this.currentFile?.properties?.name || '...';
    },

    moduleData() {
      const filterData = {
        updated: [],
        unread: [],
        unconfirmed: [],
        favorites: [],
        tags: {},
      };

      const containerData = {
        folders: [],
        files: [],
      };

      this.buildModuleData(this.currentModuleId, filterData, containerData);

      return {
        filters: [
          { text: 'contents', icon: 'mdi-folder-multiple', items: [], filter: SelectedFilter.NONE },
          {
            text: 'updatedLast5Days',
            icon: 'mdi-clock-outline',
            items: filterData.updated,
            filter: SelectedFilter.UPDATED,
          },
          {
            text: 'unread',
            icon: 'mdi-circle-medium',
            color: this.theme.unreadBadgeColor,
            items: filterData.unread,
            filter: 'unread',
          },
          {
            text: 'notAcknowledged',
            icon: 'mdi-circle-medium',
            color: this.theme.toAcknowledgeBadgeColor,
            items: filterData.unconfirmed,
            filter: 'unconfirmed',
          },
          {
            text: 'favorites',
            icon: this.mx_icons.favoriteIcon,
            color: this.theme.elements.activeColor,
            items: filterData.favorites,
            filter: 'favorites',
          },
        ],
        filterData: filterData,
        containerData: containerData,
      };
    },

    usedTags() {
      const tags = {};

      for (const [tagId, items] of Object.entries(this.moduleData.filterData.tags)) {
        const tag = this.tagsById[tagId];
        if (!tag) continue;

        const tagGroup = this.tagsById[tag.idParentFk];
        if (!tagGroup) continue;

        const groupId = tagGroup.id;
        if (!tags[groupId]) {
          tags[groupId] = {
            name: tagGroup.name,
            sort: tagGroup.sort,
            icon: 'mdi-tag-multiple',
            items: {},
          };
        }

        tags[groupId].items[tag.id] = {
          name: tag.name,
          sort: tag.sort,
          icon: 'mdi-tag',
          items: items,
        };
      }

      return tags;
    },

    availableTags() {
      return this.currentChildren.items.flatMap((currentChild) => currentChild.tags).filter((value, index, self) => self.indexOf(value) === index);
    },

    displaySideNavigation() {
      return !this.smAndDown;
    },

    showPreview() {
      return Object.keys(this.currentFile).length > 0;
    },

    displayOverflowMenu() {
      return (
        (this.currentFileProperties.confirm && this.currentFile.unconfirmed) ||
        (this.currentFileProperties.pin && !this.currentFile.favorite) ||
        (this.currentFileProperties.pin && this.currentFile.favorite)
      );
    },
  },
  methods: {
    ...mapMutations('media', ['setSorterName', 'setCmsSorterName']),
    countUnreadFiles(containerId) {
      const container = this.containersById[containerId];
      if (!container) return 0;

      let unreadCount = 0;

      if (container.type === ContainerType.FOLDER && container.children) {
        container.children.forEach((childId) => {
          unreadCount += this.countUnreadFiles(childId);
        });
      }

      if (container.unread && !container.unconfirmed) {
        unreadCount += 1;
      }

      return unreadCount;
    },

    countUnconfirmedFiles(containerId) {
      const container = this.containersById[containerId];
      if (!container) return 0;

      let unconfirmedCount = 0;

      if (container.type === ContainerType.FOLDER && container.children) {
        container.children.forEach((childId) => {
          unconfirmedCount += this.countUnconfirmedFiles(childId);
        });
      }

      if (container.unconfirmed) {
        unconfirmedCount += 1;
      }

      return unconfirmedCount;
    },

    sortFoldersAndFiles(folders, files) {
      let deviceSort = this.selectedFilter === SelectedFilter.NONE ? this.currentFolder?.properties?.deviceSort : 'a-z';

      if (deviceSort === 'id-desc') {
        deviceSort = 'updated-desc';
      }

      if (deviceSort === 'id-asc') {
        deviceSort = 'updated-asc';
      }

      mediaModuleService.sortFoldersAndFiles(deviceSort, folders, files);
    },

    sortSearchTerms(searchTerms) {
      return searchTerms.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
    },

    handleOtherFilters(folders, files, searchTerms) {
      this.moduleData.filterData[this.selectedFilter].forEach((childId) => {
        searchTerms = this.buildSearchTerms(childId, searchTerms, false);
        const childContainer = this.containersById[childId];
        if (childContainer && (!this.searchQuery || this.searchQuery === childId)) {
          childContainer.type === ContainerType.FOLDER ? folders.push(childContainer) : files.push(childContainer);
        }
      });
    },

    handleTagsFilter(folders, files, searchTerms) {
      const allContainerIdsWithTags = Object.values(this.moduleData.filterData['tags']).flat();
      allContainerIdsWithTags
        .map((containerId) => this.containersById[containerId])
        .filter((container) => container && this.selectedTags.every((tag) => container.tags.includes(tag)))
        .reduce((searchTerms, container) => {
          if (!this.searchQuery || this.searchQuery === container.id) {
            searchTerms = this.buildSearchTerms(container.id, searchTerms, false);
            if (container.type === ContainerType.FOLDER) {
              if (!folders.includes(container)) {
                folders.push(container);
              }
            } else {
              if (!files.includes(container)) {
                files.push(container);
              }
            }
          }
          return searchTerms;
        }, searchTerms);
    },

    handleNoneFilter(folders, files, searchTerms) {
      const children = this.currentFolder?.children;

      if (children) {
        children.forEach((childId) => {
          searchTerms = this.buildSearchTerms(childId, searchTerms);
          const childContainer = this.containersById[childId];
          if (childContainer && !this.searchQuery) {
            childContainer.type === ContainerType.FOLDER ? folders.push(childContainer) : files.push(childContainer);
          }
        });
      }

      if (this.searchQuery) {
        const childContainer = this.containersById[this.searchQuery];
        if (childContainer) {
          childContainer.type === ContainerType.FOLDER ? folders.push(childContainer) : files.push(childContainer);
        }
      }
    },

    buildModuleData(containerId, filterData, containerData) {
      const container = this.containersById[containerId];
      if (!container) return;

      if (Date.now() - 5 * DAY_IN_MS < Date.parse(container.updatedAt) && container.type !== ContainerType.FOLDER && container.idParentFk !== null) {
        filterData.updated.push(containerId);
      }

      if (container.unread) {
        filterData.unread.push(containerId);
      }

      if (container.unconfirmed) {
        filterData.unconfirmed.push(containerId);
      }

      if (container.favorite) {
        filterData.favorites.push(containerId);
      }

      if (container.tags) {
        for (const tagId of container.tags) {
          filterData.tags[tagId] = filterData.tags[tagId] || [];
          if (!filterData.tags[tagId].includes(containerId)) {
            filterData.tags[tagId].push(containerId);
          }
        }
      }

      if (container.children && container.type === ContainerType.FOLDER) {
        for (const childId of container.children) {
          this.buildModuleData(childId, filterData, containerData);
        }
      }
    },

    buildBreadCrumbs(containerId, items) {
      const container = this.containersById[containerId];
      if (!container) return items;

      const hrefBase = '/media/' + this.currentModuleId;
      const href = container.idParentFk ? `${hrefBase}/${containerId}` : hrefBase;

      items.push({
        id: containerId,
        text: container.properties.name,
        href,
      });

      if (container.idParentFk) {
        this.buildBreadCrumbs(container.idParentFk, items);
      }

      return items;
    },

    buildSearchTerms(containerId, searchTerms = [], recursive = true) {
      const container = this.containersById[containerId];
      if (!container) return searchTerms;

      const name = container.properties?.name;
      if (name) {
        searchTerms.push({
          name,
          id: container.id,
        });
      }

      if (recursive && container.type === ContainerType.FOLDER && container.children) {
        for (const childId of container.children) {
          this.buildSearchTerms(childId, searchTerms);
        }
      }

      return searchTerms;
    },

    getIcon(type) {
      return IconMap[type] || 'mdi-file-outline';
    },

    rowClick(row) {
      if (row.type === ContainerType.FOLDER) {
        this.resetSearch();
        this.$router.push(`/media/${this.currentModuleId}/${row.id}`);
        return;
      }

      this.$router.push(`/media/${this.currentModuleId}/${row.parentId}/${row.id}`);
    },

    resetSearch() {
      this.searchQuery = null;
    },

    removeTag(tagId) {
      this.selectedTags.splice(this.selectedTags.indexOf(tagId), 1);
    },

    resolveViewer(container) {
      const Viewers = { IMAGE: ImageViewer, VIDEO: VideoViewer, HTML: HTMLViewer, ARTICLE: ArticleViewer, PDF: PDFViewer, AUDIO: AudioViewer };

      return Viewers[container.type] ?? UnknownViewer;
    },

    setRead(containerId) {
      if (!this.currentFile.unread) return;

      this.$store.commit('media/setRead', containerId, { root: true });
      const type = this.$t(`type.${this.currentFile.type}`);
      const actionName = this.$t('action.marked');
      const state = this.$t('seen.document');
      const sendboxTitle = this.currentFile.properties.name;
      const snackbarText = this.$t('markAsMsg', { type, title: this.currentFile.properties.name, state, actionName });
      this.$store.dispatch(
        `sendbox/markAsReadMedia`,
        {
          moduleType: index.moduleType,
          moduleId: index.moduleId,
          item: this.currentFile,
          sendboxTitle,
          snackbarText,
        },
        {}
      );
    },

    setConfirmation(containerId, dialog = false) {
      if (this.currentFile && !this.currentFile.unconfirmed && !dialog) return;
      else if (this.fileBefore && !this.fileBefore.unconfirmed && dialog) this.acknowledgeDialog = false;

      this.acknowledgeDialog = false;

      this.$store.commit('media/setConfirmation', containerId, { root: true });
      const sendboxTitle = this.currentFile.properties.name;
      const snackbarText = this.$t('action.acknowledge.msg', { object: this.currentFile.properties.name });
      this.$store.dispatch(
        `sendbox/markAsAcknowledgedMedia`,
        {
          moduleType: index.moduleType,
          moduleId: index.moduleId,
          item: this.currentFile,
          sendboxTitle,
          snackbarText,
        },
        {}
      );

      this.tryGoBack();
    },

    setFavorite(containerId, value) {
      if (Object.keys(this.currentFile).length === 0) return;

      this.$store.commit('media/setFavorite', { containerId, value }, { root: true });

      const type = this.$t(`type.${this.currentFile.type}`);
      const sendboxTitle = this.currentFile.properties.name;
      const snackbarText = this.$t(value ? 'action.addFavorite.msg' : 'action.removeFavorite.msg', {
        type,
        title: this.currentFile.properties.name,
      });
      this.$store.dispatch(
        value ? `sendbox/markAsFavoriteMedia` : `sendbox/removeFromFavoriteMedia`,
        {
          moduleType: index.moduleType,
          moduleId: index.moduleId,
          item: this.currentFile,
          sendboxTitle,
          snackbarText,
        },
        {}
      );
    },
    shouldShowAcknowledgeDialog(fileId) {
      const fileBefore = this.containersById[fileId];
      const fileBeforePropertiesConfirm = fileBefore.properties && fileBefore.properties.confirm;
      if (fileBeforePropertiesConfirm && fileBefore.unconfirmed) {
        return true;
      }
      return false;
    },
    onClickBackBtn() {
      const fileId = this.$route.params.fileId;

      setTimeout(() => {
        if (!this.acknowledgeDialog && fileId && !this.shouldGoBack && this.shouldShowAcknowledgeDialog(fileId)) {
          this.shouldGoBack = true;
          this.setAcknowledgeData(fileId);
        } else {
          this.$router.back();
        }
      }, 250);
    },
    setAcknowledgeData(id) {
      if (this.shouldShowAcknowledgeDialog(id)) {
        this.fileBefore = this.containersById[id];
        this.acknowledgeDialog = true;
      }
    },
    tryGoBack() {
      if (this.shouldGoBack) {
        this.$router.back();
        setTimeout(() => {
          this.shouldGoBack = false;
        }, 100);
      }
    },
    closeAcknowledgeDialog() {
      this.acknowledgeDialog = false;
      this.tryGoBack();
    },
  },

  watch: {
    selectedFilter(to, from) {
      if (to !== from) {
        this.resetSearch();
        this.mx_isLeftDrawerOpen = false;
      }
      if (to === undefined) {
        this.selectedFilter = SelectedFilter.NONE;
      }
      if (from === 'tags' && to !== 'tags') {
        this.selectedTags = [];
      }
    },

    currentFileId(to, from) {
      if (to !== 0) {
        window.setTimeout(() => {
          this.setRead(this.currentFileId);
        }, 3000);
      }
      if (to === 0 && !this.shouldGoBack) {
        this.setAcknowledgeData(from);
      }
    },

    selectedTags(to, from) {
      if (to.length === 0) {
        this.selectedFilter = SelectedFilter.NONE;
      }
    },
  },
};
</script>

<style scoped>
.v-list-group__header__prepend-icon {
  margin-right: 12px !important;
}

.v-text-field--rounded {
  border-radius: 12px !important;
}

.v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
.v-data-table > .v-data-table__wrapper > table > tfoot > tr > td,
.v-data-table > .v-data-table__wrapper > table > thead > tr > td {
  font-size: 1rem !important;
  height: 56px !important;
  cursor: pointer;
}

a.v-breadcrumbs__item:link,
a.v-breadcrumbs__item:hover,
a.v-breadcrumbs__item:active,
a.v-breadcrumbs__item:visited {
  color: #2196f3;
}

.filter-container {
  display: flex;
  align-items: center;
  gap: 1rem;
}
</style>
